import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PreviewProvider, usePreview } from '@dashboard/context/PreviewContext';
import { AI_EDITOR_PATH, AIProvider, THEME_AI_EDITOR_PATH } from '@dashboard/context/AIContext';
import usePage, { PageProvider } from '@dashboard/hooks/usePage';
import { Share2Icon } from 'lucide-react';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { CheckoutShareButton } from '@dashboard/components/CheckoutShare';
import Spinner from '@dashboard/components/Spinner';
import { FieldsScreenSkeleton } from './components/PageFieldsForm/components/FieldsListSkeleton';
import PageTest from './scenes/PageTest';
import PagePreviewNavigation from './components/PagePreviewNavigation';
import Preview from './components/Preview';
import AIPanel from './components/AIPanel';
import { AIChangeFocus } from './components/AIPanel/useFocusAIChange';
import EditorSidebar, {
  EditorSearchButton,
  EditorTopBarStart,
  useTopBarStyle,
} from './components/EditorSidebar';
import { UnsavedChangesGuard } from '@dashboard/components/UnsavedChanges';
import { getPageTabs, hasPreview, MOVED_SECTIONS } from './editorNavigation';
import { editorLandingLocation, editorSections, firstEditorTab } from './pageEditTabs';
import usePreviewKey from './usePreviewKey';
import {
  EDITOR_FORM_COLUMN_START,
  PAGE_CONTENT_COLUMN_END,
  PAGE_CONTENT_COLUMN_START,
} from '@dashboard/layouts/NavigationParts';
import PreviewResizeHandle, { useFormColumnWidth } from './components/PreviewResizeHandle';
import SettingsModal from './components/SettingsModal/SettingsModal';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import { readNavSnapshot } from '@dashboard/lib/pageNavSnapshot';

// A tab's root redirects to its first section through the real history, which would leave
// Settings, so the screen behind a settings link opened on its own is that first section.
function firstEditorScreen(page, seller) {
  const tab = firstEditorTab(page.type);
  const [section] = editorSections(tab, { page, seller });
  return {
    pathname: `/pages/${page._id}/customize/${tab}${section?.path ?? ''}`,
    search: '',
  };
}

// Settings open in a modal over the editor screen the merchant was on, or over the first screen
// when a settings link is opened on its own. The screen behind is what the editor renders.
function useSettingsModal(page, seller) {
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const lastScreen = useRef(null);
  const base = `/pages/${match.params.id}/customize`;

  const redirects = MOVED_SECTIONS.some(({ from }) =>
    location.pathname.startsWith(`${base}/${from}`),
  );
  const settings = redirects
    ? null
    : matchPath(location.pathname, { path: `${base}/settings/:setting?` });
  if (!settings && !redirects && location.pathname.startsWith(`${base}/`)) {
    lastScreen.current = location;
  }

  const background =
    settings && page?.type ? (lastScreen.current ?? firstEditorScreen(page, seller)) : location;

  return {
    background,
    open: Boolean(settings),
    setting: settings?.params.setting,
    select: setting =>
      history.push({ pathname: `${base}/settings/${setting}`, search: location.search }),
    back: () => history.push({ pathname: `${base}/settings`, search: location.search }),
    close: () => history.push(background),
  };
}

const PageEditContent = () => {
  const { page, isLoading } = usePage();
  const topBarStyle = useTopBarStyle();
  const match = useRouteMatch();
  const { seller } = useAuth();
  const settingsModal = useSettingsModal(page, seller);
  const location = settingsModal.background;
  const [preview] = usePreview();
  const { isFullscreen } = preview;

  const [previewKey, setPreviewKey] = usePreviewKey(page, [seller]);
  // Before the page loads, its last known shape draws the sidebar, so a refresh doesn't flash it.
  const navPage = !isLoading && page?._id ? page : readNavSnapshot(match.params.id);

  return (
    <div>
      {!isLoading && page?._id && (
        <TrackRecent
          type="checkout-page"
          id={page._id}
          label={page.name}
          subtitle={page.type}
          path={`/pages/${page._id}/customize`}
        />
      )}
      {/* Hide all navigation when in fullscreen */}
      {!isFullscreen && (
        <>
          <div className="relative">
            <nav
              className="flex h-14 border-b border-gray-200 dark:border-border px-4 items-center gap-x-4 fixed z-40 bg-white dark:bg-background transition-[left] duration-200 ease-linear"
              style={topBarStyle}
            >
              <EditorTopBarStart />

              <div
                className="hidden w-full max-w-xs md:block"
                style={
                  !hasPreview(location.pathname, match.params.id)
                    ? PAGE_CONTENT_COLUMN_START
                    : EDITOR_FORM_COLUMN_START
                }
              >
                <EditorSearchButton />
              </div>

              {!isLoading && (
                <>
                  <div
                    className="ml-auto flex flex-shrink-0 items-center space-x-3"
                    style={
                      page?._id && !hasPreview(location.pathname, page._id)
                        ? PAGE_CONTENT_COLUMN_END
                        : undefined
                    }
                  >
                    <CheckoutShareButton appearance="primary" checkoutId={match.params.id}>
                      <Share2Icon className="w-3.5 h-3.5 mr-1.5" />
                      {page.type === 'event'
                        ? 'Share event'
                        : page.type === 'form'
                          ? 'Share form'
                          : 'Share checkout'}
                    </CheckoutShareButton>
                  </div>
                </>
              )}
            </nav>
          </div>
        </>
      )}

      <div className="flex">
        {navPage && !isFullscreen && <EditorSidebar page={navPage} />}

        <div className="min-w-0 flex-1">
          <MainContent
            location={location}
            page={page}
            seller={seller}
            isLoading={isLoading}
            previewKey={previewKey}
            setPreviewKey={setPreviewKey}
          />
        </div>
      </div>

      {!isLoading && page?._id && (
        <SettingsModal
          open={settingsModal.open}
          setting={settingsModal.setting}
          onSelect={settingsModal.select}
          onBack={settingsModal.back}
          onClose={settingsModal.close}
        />
      )}
    </div>
  );
};

const PageEdit = () => {
  const { page } = usePage();

  return (
    <PreviewProvider page={page}>
      <AIProvider page={page}>
        <UnsavedChangesGuard>
          <PageEditContent />
        </UnsavedChangesGuard>
      </AIProvider>
    </PreviewProvider>
  );
};

function MainContent({ location, page, seller, isLoading, previewKey, setPreviewKey }) {
  const { pathname } = location;
  const showsPreview = isLoading || !page?._id || hasPreview(pathname, page._id);
  const [preview] = usePreview();
  const { isFullscreen } = preview;
  const [formWidth, setFormWidth] = useFormColumnWidth();

  // Lock body scroll when fullscreen preview is open so scrolling outside
  // the preview box doesn't scroll the form underneath. Restore scroll
  // position of the left panel (form) when exiting fullscreen.
  const savedScrollTop = useRef(0);
  useEffect(() => {
    // The left panel's scrollable ancestor is the document/window
    if (isFullscreen) {
      savedScrollTop.current = window.scrollY;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollTop.current);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  return (
    <div className="flex items-start">
      {/* The form keeps its width; the preview takes whatever the sidebar leaves. */}
      <div
        className={clsx(
          'h-full w-full',
          showsPreview ? 'lg:w-[var(--form-width)] lg:flex-shrink-0' : 'min-w-0 flex-1',
        )}
        style={showsPreview ? { '--form-width': `${formWidth}px` } : undefined}
      >
        <div className="h-full pt-14">
          {isLoading && pathname.includes('/customize/fields') ? (
            <FieldsScreenSkeleton />
          ) : isLoading ? (
            <div className="w-full h-[calc(100vh_-_120px_-_var(--top-banner-h,0px))] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <CheckoutEdit location={location} page={page} seller={seller} />
          )}
        </div>
      </div>

      {showsPreview && (
        <>
          {/* Sticky is removed during fullscreen so the fixed preview container
              can escape the stacking context and cover the entire viewport. */}
          <div
            className={clsx(
              'pl-4 pr-4 min-w-0 flex-1 top-[var(--top-banner-h,0px)] right-0 bottom-0 relative',
              'lg:bg-neutralgray dark:lg:bg-background',
              'pt-14 lg:flex lg:flex-col overflow-visible min-h-[calc(100vh_-_var(--top-banner-h,0px))] border-l border-gray-100 dark:border-border',
              {
                // Phones only reach the preview through fullscreen (the AI editor's Preview button).
                hidden: !isFullscreen,
                'lg:sticky': !isFullscreen,
              },
            )}
          >
            {!isFullscreen && <PreviewResizeHandle width={formWidth} onChange={setFormWidth} />}

            {!isFullscreen && (
              <div className="w-full bg-neutralgray dark:bg-background h-auto">
                <PagePreviewNavigation page={page} setPreviewKey={setPreviewKey} />
              </div>
            )}

            <div
              className={clsx({
                'hidden lg:block h-full flex-1': !isFullscreen,
                'fixed inset-0 z-50 bg-neutralgray dark:bg-background flex flex-col': isFullscreen,
              })}
            >
              {!isLoading && (
                <Preview
                  page={page}
                  seller={seller}
                  previewKey={previewKey}
                  setPreviewKey={setPreviewKey}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CheckoutEdit({ location, page, seller }) {
  const match = useRouteMatch();
  const { isAuthenticating } = useAuth();

  const tabs = getPageTabs(page);
  const firstTab = firstEditorTab(page.type);
  // Create with AI and AIGenerate send a new page here with ?ai=1, so it opens on the AI editor.
  const opensOnAI = new URLSearchParams(location.search).get('ai') === '1';
  const landing = editorLandingLocation({
    pageId: match.params.id,
    pageType: page.type,
    search: location.search,
    opensOnAI,
  });

  return (
    <div>
      {location.state?.aiFocus && (
        <AIChangeFocus key={location.key} target={location.state.aiFocus} />
      )}
      <Switch location={location}>
        <Route
          exact
          path={[AI_EDITOR_PATH, THEME_AI_EDITOR_PATH].map(
            path => `/pages/${match.params.id}/customize${path}`,
          )}
        >
          <AIPanel />
        </Route>

        {MOVED_SECTIONS.map(({ from, to }) => (
          <Route
            key={from}
            path={`/pages/${match.params.id}/customize/${from}`}
            render={({ location }) => (
              <Redirect
                to={{
                  ...location,
                  pathname: `/pages/${match.params.id}/customize/${to}`,
                }}
              />
            )}
          />
        ))}

        {tabs
          .filter(tab => tab.path !== 'settings')
          .map(tab => (
            <Route path={`/pages/${match.params.id}/customize/${tab.path}`} key={tab.path}>
              <tab.component page={page} product={page?.product} seller={seller} />
            </Route>
          ))}

        <Route path={`/pages/${match.params.id}/customize/test`}>
          <PageTest page={page} />
        </Route>

        {firstTab && !(opensOnAI && isAuthenticating) && (
          <Redirect from={`/pages/${match.params.id}`} to={landing} />
        )}
      </Switch>
    </div>
  );
}

const CheckoutContainer = () => {
  const match = useRouteMatch();

  return (
    <PageProvider pageId={match.params.id}>
      <PageEdit />
    </PageProvider>
  );
};

export default CheckoutContainer;
